import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Chip, Icon, ListGroup, ListRow } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from './api.ts';
import { usePrefs } from './prefs.tsx';
import { CopyText } from './servers-ui.tsx';
import { haptic } from './telegram.ts';

type Range = '1h' | '24h' | '7d';

function useBytes() {
  const { t } = usePrefs();
  return (n: number, perSecond = false) => {
    let v = n;
    let i = 0;
    while (v >= 1024 && i < t.server.bytes.length - 1) {
      v /= 1024;
      i++;
    }
    const num = v >= 100 || i === 0 ? Math.round(v).toString() : v.toFixed(1);
    return `${num} ${t.server.bytes[i]}${perSecond ? t.server.perSecond : ''}`;
  };
}

/** A small area chart; `max` fixes the top (100 for percentages), otherwise it fits the data. */
function Spark({ values, max, tone = 'accent' }: { values: number[]; max?: number; tone?: 'accent' | 'ok' }) {
  const W = 300;
  const H = 64;
  if (values.length < 2) return <div className="ul-spark ul-spark--empty" />;
  const top = max ?? Math.max(1, ...values) * 1.15;
  const step = W / (values.length - 1);
  const pts = values.map(
    (v, i) => `${(i * step).toFixed(1)},${(H - (Math.min(v, top) / top) * (H - 4) - 2).toFixed(1)}`,
  );
  return (
    <svg
      className={`ul-spark ul-spark--${tone}`}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <polygon points={`0,${H} ${pts.join(' ')} ${W},${H}`} className="ul-spark__area" />
      <polyline points={pts.join(' ')} className="ul-spark__line" />
    </svg>
  );
}

function Metric({
  label,
  value,
  sub,
  values,
  max,
}: {
  label: string;
  value: string;
  sub?: string;
  values: number[];
  max?: number;
}) {
  return (
    <div className="ul-metric">
      <span className="ul-kpi__label">{label}</span>
      <span className="ul-metric__value">{value}</span>
      {sub ? <span className="ul-kpi__sub">{sub}</span> : null}
      <Spark values={values} max={max} />
    </div>
  );
}

/**
 * Monitoring on the server screen: one command installs the agent, then live tiles and charts.
 * The command carries a new token each time it is shown, so the old one stops working.
 */
export function MonitoringSection({ id }: { id: string }) {
  const trpc = useTRPC();
  const { t, dateTime } = usePrefs();
  const bytes = useBytes();
  const [range, setRange] = useState<Range>('1h');
  const [command, setCommand] = useState<{ install: string; uninstall: string } | null>(null);
  const [showRemove, setShowRemove] = useState(false);
  const mon = useQuery({
    ...trpc.servers.monitoring.queryOptions({ id, range }),
    // Waiting for the first report: look often; afterwards once a minute is enough
    refetchInterval: (q) => (q.state.data?.latest ? 60_000 : command ? 5_000 : false),
  });
  const issue = useMutation(
    trpc.servers.monitoringCommand.mutationOptions({
      onSuccess: (c) => {
        haptic.success();
        setCommand(c);
      },
      onError: () => haptic.error(),
    }),
  );

  const m = mon.data;
  const latest = m?.latest;

  // Not set up yet, or waiting for the agent's first report
  if (!latest) {
    return (
      <section className="ul-stack" aria-labelledby="srv-mon">
        <h2 className="ul-group__header" id="srv-mon">
          {t.server.monitoring}
        </h2>
        <div className="ul-mon-intro">
          <Icon name="chart" size={22} />
          <span>{t.server.monitoringIntro}</span>
        </div>
        {command ? (
          <>
            <ol className="ul-guide">
              {t.server.monitoringSteps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            <ListGroup footer={t.server.monitoringNote}>
              <ListRow
                icon="terminal"
                title={<span className="ul-mono ul-wrap">{command.install}</span>}
                trailing={<CopyText text={command.install} />}
              />
            </ListGroup>
            <div className="ul-wait-inline" aria-live="polite">
              <span className="ul-spinner" aria-hidden />
              {t.server.monitoringWaiting}
            </div>
          </>
        ) : (
          <Button block icon="chart" loading={issue.isPending} onClick={() => issue.mutate({ id })}>
            {m?.issued ? t.server.monitoringShowCommand : t.server.monitoringConnect}
          </Button>
        )}
        {issue.isError ? (
          <div className="ul-error" role="alert">
            {errorText(issue.error)}
          </div>
        ) : null}
      </section>
    );
  }

  const pts = m.points;
  return (
    <section className="ul-stack" aria-labelledby="srv-mon">
      <div className="ul-sechead">
        <h2 className="ul-group__header" id="srv-mon" style={{ padding: 0 }}>
          {t.server.monitoring}
        </h2>
        <span className={`ul-mon-status ${m.online ? 'is-online' : 'is-offline'}`}>
          <span className="ul-mon-status__dot" aria-hidden />
          {m.online ? t.server.monitoringOnline : t.server.monitoringOffline(dateTime(m.lastSeenAt!))}
        </span>
      </div>
      <div className="ul-chips">
        {(['1h', '24h', '7d'] as Range[]).map((r) => (
          <Chip key={r} selected={r === range} onClick={() => setRange(r)}>
            {t.server.ranges[r]}
          </Chip>
        ))}
      </div>
      <div className="ul-kpis">
        <Metric
          label={t.server.cpu}
          value={`${Math.round(latest.cpu)}%`}
          sub={latest.load1 != null ? `load ${latest.load1}` : undefined}
          values={pts.map((p) => p.cpu)}
          max={100}
        />
        <Metric
          label={t.server.ram}
          value={`${Math.round((100 * latest.memUsed) / latest.memTotal)}%`}
          sub={`${bytes(latest.memUsed)} / ${bytes(latest.memTotal)}`}
          values={pts.map((p) => p.memPct)}
          max={100}
        />
        <Metric
          label={t.server.disk}
          value={`${Math.round((100 * latest.diskUsed) / latest.diskTotal)}%`}
          sub={`${bytes(latest.diskUsed)} / ${bytes(latest.diskTotal)}`}
          values={pts.map((p) => p.diskPct)}
          max={100}
        />
        <Metric
          label={t.server.net}
          value={bytes(latest.netRx + latest.netTx, true)}
          sub={`${t.server.netIn} ${bytes(latest.netRx, true)} · ${t.server.netOut} ${bytes(latest.netTx, true)}`}
          values={pts.map((p) => p.netRx + p.netTx)}
        />
      </div>
      {showRemove ? (
        <ListGroup header={t.server.monitoringRemoveHint}>
          <ListRow
            icon="terminal"
            title={<span className="ul-mono ul-wrap">{m.uninstall}</span>}
            trailing={<CopyText text={m.uninstall} />}
          />
        </ListGroup>
      ) : (
        <button type="button" className="ul-link" onClick={() => setShowRemove(true)}>
          {t.server.monitoringRemove}
        </button>
      )}
    </section>
  );
}
